/**
 * API client for the admin portal.
 *
 * Talks to the single shared NestJS backend (InviteCloudBackend) at
 * `${VITE_API_URL}/v1/admin/*`. Every request carries a Firebase ID token;
 * the backend's FirebaseAuthGuard + RolesGuard + AdminAccessGuard enforce
 * that the caller has an ADMIN/SUPER_ADMIN role.
 *
 * Response envelope: the backend wraps every response in `{ data, ... }`
 * via TransformInterceptor. We unwrap `data` here so callers see the raw
 * payload.
 */

import { auth } from '@/src/infrastructure/config/firebase';
import { signOut } from 'firebase/auth';

export const API_BASE =
  (import.meta.env.VITE_API_URL || 'http://localhost:4000') + '/v1';

async function request<T>(
  url: string,
  options: RequestInit = {},
  retried = false,
): Promise<T> {
  const isBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
  const token = isBypass
    ? 'dev-token'
    : await auth.currentUser?.getIdToken(retried);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && !retried && !isBypass && auth.currentUser) {
    return request<T>(url, options, true);
  }
  if (res.status === 401 || res.status === 403) {
    if (isBypass) {
      throw new Error(`Unauthorized (HTTP ${res.status}): ensure backend is also running with DEV_AUTH_BYPASS=true and the dev-user exists in the database`);
    }
    await signOut(auth).catch(() => undefined);
    if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
    throw new Error(res.status === 403 ? 'Admin access required' : 'Session expired');
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const message = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message ?? `HTTP ${res.status}`;
    throw new Error(message);
  }

  if (res.status === 204) return undefined as T;
  const body = await res.json();
  return (body?.data !== undefined ? body.data : body) as T;
}

// ── Types ────────────────────────────────────────────────────────────────────

export interface Paginated<T> {
  items: T[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  firebaseUid: string | null;
  avatar: string | null;
  role: string;
  plan: string;
  isBlocked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AdminInvitation {
  id: string;
  eventName: string;
  occasionType: string;
  status: string;
  isDisabled: boolean;
  publicSlug: string | null;
  hostedUrl: string | null;
  eventDate: string;
  // Invitation Management System: lifecycle + analytics fields.
  expiresAt?: string | null;
  publishedAt?: string | null;
  archivedAt?: string | null;
  suspendedAt?: string | null;
  suspensionReason?: string | null;
  totalViews?: number;
  totalShares?: number;
  totalRsvps?: number;
  reportedCount?: number;
  updatedAt?: string;
  createdAt: string;
  user: { id: string; email: string; name: string };
  template: { id: string; name: string; occasionType: string };
}

export interface AdminInvitationStats {
  total: number;
  byStatus: Record<string, number>;
  reported: number;
  suspended: number;
}

export interface AdminInvitationHistoryEntry {
  id: string;
  action: string;
  actorId: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface AdminInvitationListQuery {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  userId?: string;
  eventType?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export type AdminBulkAction = 'archive' | 'suspend' | 'restore';

export interface AdminTemplate {
  id: string;
  name: string;
  slug: string | null;
  occasionType: string;
  isPremium: boolean;
  isActive: boolean;
  publishedCount?: number;
  draftCount?: number;
  previewImageUrl: string;
  description: string;
  renderMode?: string;
  capabilities?: Record<string, unknown>;
  createdAt: string;
}

export interface AdminTemplatePublishedSite {
  id: string;
  userId: string;
  publicSlug: string | null;
  eventName: string | null;
  eventDate: string | null;
  status: string;
  hostedUrl: string | null;
  publishedAt: string | null;
}


export interface TopTemplate {
  templateId: string;
  name: string;
  previewImageUrl: string | null;
  invitationCount: number;
  sharePercent: number;
}

export interface AiJobLog {
  id: string;
  invitationId: string;
  eventName: string;
  userEmail: string;
  templateName: string;
  status: string;
  providerUsed: string;
  modelUsed: string | null;
  costUsd: number;
  costInr: number;
  creditsUsed: number;
  promptTokens: number;
  outputTokens: number;
  imagesGenerated: number;
  retryCount: number;
  createdAt: string;
}

export interface AiCreditStats {
  totalCreditsUsed: number;
  totalCostUsd: number;
  totalCostInr: number;
  totalJobs: number;
  completedJobs: number;
  failedJobs: number;
  runningJobs: number;
  totalPromptTokens: number;
  totalOutputTokens: number;
  totalImagesGenerated: number;
  totalRetries: number;
  avgCostPerImageUsd: number;
  byTemplate: Array<{
    templateId: string;
    templateName: string;
    occasionType: string;
    jobCount: number;
    totalCostUsd: number;
    totalCostInr: number;
    creditsUsed: number;
    totalPromptTokens: number;
    totalOutputTokens: number;
    imagesGenerated: number;
    retryCount: number;
    retryRate: number;
  }>;
  byProvider: Array<{
    providerName: string;
    jobCount: number;
    totalCostUsd: number;
    imagesGenerated: number;
    totalPromptTokens: number;
    totalOutputTokens: number;
    avgCostPerImage: number;
  }>;
  recentJobs: AiJobLog[];
}

export interface DashboardStats {
  totalUsers: number;
  totalInvitations: number;
  totalMedia: number;
  rsvpAnalytics: Array<{ status: string; _count: { status: number } }>;
  topTemplates: TopTemplate[];
  aiCreditStats?: AiCreditStats;
}

export interface UserGrowthPoint {
  date: string;
  count: number;
}

// ── Endpoints ────────────────────────────────────────────────────────────────

export function getDashboardStats() {
  return request<DashboardStats>(`${API_BASE}/admin/dashboard/stats`);
}

export function getAiCreditStats() {
  return request<AiCreditStats>(`${API_BASE}/admin/dashboard/ai-credits`);
}

export interface GA4Overview {
  activeUsers: number;
  newUsers: number;
  sessions: number;
  pageViews: number;
  avgEngagementTime: number;
  bounceRate: number;
}

export interface GA4Report {
  overview: GA4Overview;
  traffic: {
    referrers: Array<{ name: string; value: number }>;
    countries: Array<{ name: string; value: number }>;
    devices: Array<{ name: string; value: number }>;
    browsers: Array<{ name: string; value: number }>;
    os: Array<{ name: string; value: number }>;
  };
  funnel: Array<{ step: string; count: number; percent: number }>;
  events: Array<{ name: string; value: number }>;
  templates: Array<{ templateId: string; selections: number; drafts: number; conversionRate: number }>;
  viralLoop?: {
    invitationViews: number;
    rsvpLandings: number;
    /** false while the app never fires RSVP_LANDING_VIEW: show "not tracked yet", not 0. */
    rsvpLandingsTracked: boolean;
    viralSignups: number;
    kFactor: number;
  };
  isMocked: boolean;
  /** 'unconfigured' = GA4 env not set; 'error' = GA4 request failed at runtime. */
  mockReason?: 'unconfigured' | 'error' | null;
}

export function getGA4Overview(days = 30) {
  return request<GA4Report>(`${API_BASE}/admin/dashboard/ga4-overview?days=${days}`);
}

export function getUserGrowth(days: 30 | 90 = 30) {
  return request<UserGrowthPoint[]>(`${API_BASE}/admin/dashboard/user-growth?days=${days}`);
}

export function getRecentUsers() {
  return request<AdminUser[]>(`${API_BASE}/admin/dashboard/recent-users`);
}

export function getRecentInvitations() {
  return request<AdminInvitation[]>(`${API_BASE}/admin/dashboard/recent-invitations`);
}

export function getRecentMedia() {
  return request<unknown[]>(`${API_BASE}/admin/dashboard/recent-media`);
}

export function listUsers(params: { page?: number; limit?: number; search?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);
  return request<Paginated<AdminUser>>(`${API_BASE}/admin/users?${qs}`);
}

export function blockUser(id: string) {
  return request<AdminUser>(`${API_BASE}/admin/users/${id}/block`, { method: 'PATCH' });
}

export function unblockUser(id: string) {
  return request<AdminUser>(`${API_BASE}/admin/users/${id}/unblock`, { method: 'PATCH' });
}

export function listInvitations(params: AdminInvitationListQuery = {}) {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      qs.set(key, String(value));
    }
  }
  return request<Paginated<AdminInvitation>>(`${API_BASE}/admin/invitations?${qs}`);
}

export function getInvitationStats() {
  return request<AdminInvitationStats>(`${API_BASE}/admin/invitations/stats`);
}

export function getInvitationDetails(id: string) {
  return request<AdminInvitation & { rsvps?: unknown[]; template?: unknown }>(
    `${API_BASE}/admin/invitations/${id}`,
  );
}

export function getInvitationHistory(id: string) {
  return request<AdminInvitationHistoryEntry[]>(
    `${API_BASE}/admin/invitations/${id}/history`,
  );
}

export function archiveInvitation(id: string) {
  return request<AdminInvitation>(`${API_BASE}/admin/invitations/${id}/archive`, {
    method: 'POST',
  });
}

export function suspendInvitation(id: string, reason?: string) {
  return request<AdminInvitation>(`${API_BASE}/admin/invitations/${id}/suspend`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function restoreInvitation(id: string) {
  return request<AdminInvitation>(`${API_BASE}/admin/invitations/${id}/restore`, {
    method: 'POST',
  });
}

export function bulkInvitationAction(
  ids: string[],
  action: AdminBulkAction,
  reason?: string,
) {
  return request<Array<{ id: string; ok: boolean; error?: string }>>(
    `${API_BASE}/admin/invitations/bulk`,
    { method: 'POST', body: JSON.stringify({ ids, action, reason }) },
  );
}

// Legacy compatibility for older callers. Both back-compat to the new
// suspend/restore moderation surface on the backend.
export function disableInvitation(id: string) {
  return suspendInvitation(id);
}

export function enableInvitation(id: string) {
  return restoreInvitation(id);
}

export function listTemplates(params: { page?: number; limit?: number; search?: string; isActive?: boolean } = {}) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);
  if (params.isActive !== undefined) qs.set('isActive', String(params.isActive));
  return request<Paginated<AdminTemplate>>(`${API_BASE}/admin/templates?${qs}`);
}

export function updateTemplateStatus(id: string, isActive: boolean) {
  return request<AdminTemplate>(`${API_BASE}/admin/templates/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ isActive }),
  });
}

export function getTemplatePublishedSites(id: string) {
  return request<AdminTemplatePublishedSite[]>(`${API_BASE}/admin/templates/${id}/published-sites`);
}

export function stopTemplatePublishedSites(id: string, reason?: string) {
  return request<{ templateId: string; stoppedCount: number; suspendedIds: string[] }>(
    `${API_BASE}/admin/templates/${id}/stop-published-sites`,
    {
      method: 'POST',
      body: JSON.stringify({ reason }),
    },
  );
}


// ── Notifications (admin's own inbox) ───────────────────────────────────────
// These hit the shared `/v1/notifications/*` endpoints; the backend scopes
// every query by the authenticated user, so an admin only sees notifications
// addressed to them (e.g. new critical feedback, broadcasts, system alerts).

export interface ApiNotification {
  id: string;
  type: string;
  category: string;
  title: string;
  message: string;
  metadata: Record<string, unknown>;
  isRead: boolean;
  readAt: string | null;
  createdAt: string;
}

export interface NotificationsPage {
  items: ApiNotification[];
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  unreadCount: number;
}

export function getNotifications(params: { page?: number; limit?: number; unreadOnly?: boolean } = {}) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.unreadOnly) qs.set('unreadOnly', 'true');
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return request<NotificationsPage>(`${API_BASE}/notifications${suffix}`);
}

export function getUnreadNotificationCount() {
  return request<{ unreadCount: number }>(`${API_BASE}/notifications/unread-count`);
}

export function markNotificationRead(id: string) {
  return request<ApiNotification>(`${API_BASE}/notifications/${id}/read`, { method: 'PATCH' });
}

export function markAllNotificationsRead() {
  return request<{ updated: number }>(`${API_BASE}/notifications/read-all`, { method: 'PATCH' });
}

export function deleteNotification(id: string) {
  return request<{ id: string; deleted: true }>(`${API_BASE}/notifications/${id}`, {
    method: 'DELETE',
  });
}

// `/admin/auth/me` is the cheapest way for the client to confirm the current
// Firebase user is recognised by the backend AND has the admin role — if the
// guard chain rejects, the request returns 401/403 which the client uses to
// short-circuit login.
export function verifyAdmin() {
  return request<{ authenticated: boolean }>(`${API_BASE}/admin/auth/me`);
}

/* ──────────────────────────────────────────────────────────── Feedback ── */

export type AdminFeedbackType =
  | 'BUG'
  | 'FEATURE_REQUEST'
  | 'GENERAL'
  | 'PAYMENT_ISSUE'
  | 'PERFORMANCE'
  | 'UI_UX'
  | 'OTHER';

export type AdminFeedbackStatus =
  | 'OPEN'
  | 'IN_REVIEW'
  | 'RESOLVED'
  | 'REJECTED'
  | 'CLOSED';

export type AdminFeedbackPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export interface AdminFeedbackAttachment {
  id: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  createdAt: string;
}

export interface AdminFeedbackActivity {
  id: string;
  action: string;
  performedById: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface AdminFeedback {
  id: string;
  userId: string;
  user: { id: string; email: string; name: string; avatar: string | null } | null;
  type: AdminFeedbackType;
  title: string;
  description: string;
  rating: number | null;
  status: AdminFeedbackStatus;
  priority: AdminFeedbackPriority;
  adminResponse: string | null;
  metadata: Record<string, unknown>;
  attachments: AdminFeedbackAttachment[];
  activity?: AdminFeedbackActivity[];
  resolvedAt: string | null;
  resolvedBy?: { id: string; email: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackStats {
  total: number;
  byStatus: Record<string, number>;
  byType: Record<string, number>;
  byPriority: Record<string, number>;
  averageRating: number | null;
  ratingCount: number;
}

export interface ListFeedbackParams {
  page?: number;
  limit?: number;
  status?: AdminFeedbackStatus;
  type?: AdminFeedbackType;
  priority?: AdminFeedbackPriority;
  rating?: number;
  search?: string;
  from?: string;
  to?: string;
}

function feedbackQs(params: ListFeedbackParams) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.status) qs.set('status', params.status);
  if (params.type) qs.set('type', params.type);
  if (params.priority) qs.set('priority', params.priority);
  if (params.rating) qs.set('rating', String(params.rating));
  if (params.search) qs.set('search', params.search);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  return qs.toString();
}

export function listFeedback(params: ListFeedbackParams = {}) {
  return request<Paginated<AdminFeedback>>(
    `${API_BASE}/admin/feedback?${feedbackQs(params)}`,
  );
}

export function getFeedbackStats() {
  return request<FeedbackStats>(`${API_BASE}/admin/feedback/stats`);
}

export function getFeedback(id: string) {
  return request<AdminFeedback>(`${API_BASE}/admin/feedback/${id}`);
}

export function updateFeedbackStatus(
  id: string,
  status: AdminFeedbackStatus,
  note?: string,
) {
  return request<AdminFeedback>(`${API_BASE}/admin/feedback/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, note }),
  });
}

export function updateFeedbackPriority(id: string, priority: AdminFeedbackPriority) {
  return request<AdminFeedback>(`${API_BASE}/admin/feedback/${id}/priority`, {
    method: 'PATCH',
    body: JSON.stringify({ priority }),
  });
}

export function respondToFeedback(
  id: string,
  message: string,
  status?: AdminFeedbackStatus,
) {
  return request<AdminFeedback>(`${API_BASE}/admin/feedback/${id}/respond`, {
    method: 'POST',
    body: JSON.stringify({ message, status }),
  });
}

export function deleteFeedback(id: string) {
  return request<void>(`${API_BASE}/admin/feedback/${id}`, { method: 'DELETE' });
}

// ── Media / R2 storage (admin) ──────────────────────────────────────────────

export interface AdminMediaAsset {
  id: string;
  invitationId: string;
  originalKey: string;
  processedKey: string;
  thumbnailKey: string;
  mimeType: string;
  sizeBytes: number;
  markedForDeletion: boolean;
  deletedAt: string | null;
  createdAt: string;
}

export interface MediaStorageBucket {
  imageCount: number;
  videoCount: number;
  imageBytes: number;
  videoBytes: number;
  totalBytes: number;
}

export interface MediaStorageUserRow extends MediaStorageBucket {
  userId: string;
  email: string;
  name: string;
  assetCount: number;
}

export interface MediaStorageTemplateRow extends MediaStorageBucket {
  templateId: string;
  templateName: string;
  assetCount: number;
}

export interface MediaStorageInvitationRow extends MediaStorageBucket {
  invitationId: string;
  eventName: string;
  userId: string;
  userEmail: string;
  templateId: string;
  templateName: string;
  assetCount: number;
}

export interface MediaStorageStats {
  totals: MediaStorageBucket & { assetCount: number };
  byUser: MediaStorageUserRow[];
  byTemplate: MediaStorageTemplateRow[];
  byInvitation: MediaStorageInvitationRow[];
}

export function getMediaStorageStats() {
  return request<MediaStorageStats>(`${API_BASE}/admin/media/storage-stats`);
}

export function listMediaAssets(params: { page?: number; limit?: number; search?: string; fileType?: string } = {}) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.search) qs.set('search', params.search);
  if (params.fileType) qs.set('fileType', params.fileType);
  return request<Paginated<AdminMediaAsset>>(`${API_BASE}/admin/media?${qs}`);
}

export function markMediaDeleted(id: string) {
  return request<void>(`${API_BASE}/admin/media/${id}`, { method: 'DELETE' });
}

// First-login bootstrap: creates the local user row keyed off the Firebase
// identity. Idempotent server-side, so it is safe to call after every sign-in
// — the backend updates the row's display name / avatar from the token and
// returns the projected profile. Without this call `verifyAdmin()` 404s
// because no DB row exists for the Firebase uid yet.
export interface SyncUserHints {
  displayName?: string;
  avatar?: string;
  provider?: string;
}
export function syncUser(hints: SyncUserHints = {}) {
  return request<{ id: string; email: string; role: string }>(
    `${API_BASE}/auth/sync-user`,
    {
      method: 'POST',
      body: JSON.stringify(hints),
    },
  );
}

// ── Help & Support (admin) ──────────────────────────────────────────────────

export type AdminSupportTicketStatus =
  | 'OPEN'
  | 'IN_PROGRESS'
  | 'WAITING_FOR_USER'
  | 'RESOLVED'
  | 'CLOSED';
export type AdminSupportTicketPriority = 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
export type AdminSupportTicketCategory =
  | 'ACCOUNT'
  | 'BILLING'
  | 'TEMPLATE'
  | 'INVITATION'
  | 'PUBLISHING'
  | 'RSVP'
  | 'MEDIA'
  | 'BUG'
  | 'FEATURE_REQUEST'
  | 'OTHER';
export type AdminSupportSenderType = 'USER' | 'ADMIN' | 'SYSTEM';

export interface AdminSupportAttachment {
  id: string;
  fileUrl: string;
  fileType: string;
  fileSize: number;
  createdAt: string;
}

export interface AdminSupportMessage {
  id: string;
  ticketId: string;
  senderType: AdminSupportSenderType;
  senderId: string | null;
  sender: { id: string; email: string; name: string; avatar: string | null; role?: string } | null;
  message: string;
  internalNote: boolean;
  attachments: AdminSupportAttachment[];
  createdAt: string;
}

export interface AdminSupportActivity {
  id: string;
  action: string;
  performedById: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface AdminSupportTicket {
  id: string;
  userId: string;
  user: { id: string; email: string; name: string; avatar: string | null } | null;
  subject: string;
  category: AdminSupportTicketCategory;
  priority: AdminSupportTicketPriority;
  status: AdminSupportTicketStatus;
  description: string;
  assignedAdminId: string | null;
  assignedAdmin: { id: string; email: string; name: string; avatar: string | null } | null;
  metadata: Record<string, unknown>;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  lastActivityAt: string;
  messageCount: number;
  messages: AdminSupportMessage[];
  activity?: AdminSupportActivity[];
  createdAt: string;
  updatedAt: string;
}

export interface SupportStats {
  total: number;
  openCount: number;
  unassignedOpen: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  byCategory: Record<string, number>;
}

export interface ListSupportTicketsParams {
  page?: number;
  limit?: number;
  status?: AdminSupportTicketStatus;
  category?: AdminSupportTicketCategory;
  priority?: AdminSupportTicketPriority;
  assignedAdminId?: string;
  search?: string;
}

function supportQs(params: ListSupportTicketsParams = {}) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.status) qs.set('status', params.status);
  if (params.category) qs.set('category', params.category);
  if (params.priority) qs.set('priority', params.priority);
  if (params.assignedAdminId) qs.set('assignedAdminId', params.assignedAdminId);
  if (params.search) qs.set('search', params.search);
  return qs.toString();
}

export function listSupportTickets(params: ListSupportTicketsParams = {}) {
  return request<Paginated<AdminSupportTicket>>(
    `${API_BASE}/admin/support/tickets?${supportQs(params)}`,
  );
}

export function getSupportStats() {
  return request<SupportStats>(`${API_BASE}/admin/support/tickets/stats`);
}

export function getSupportTicket(id: string) {
  return request<AdminSupportTicket>(`${API_BASE}/admin/support/tickets/${id}`);
}

export function replyToSupportTicket(
  id: string,
  payload: { message: string; internalNote?: boolean; attachments?: { fileUrl: string; fileType: string; fileSize: number }[] },
) {
  return request<AdminSupportTicket>(`${API_BASE}/admin/support/tickets/${id}/messages`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateSupportTicketStatus(
  id: string,
  status: AdminSupportTicketStatus,
  note?: string,
) {
  return request<AdminSupportTicket>(`${API_BASE}/admin/support/tickets/${id}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status, note }),
  });
}

export function updateSupportTicketPriority(
  id: string,
  priority: AdminSupportTicketPriority,
) {
  return request<AdminSupportTicket>(`${API_BASE}/admin/support/tickets/${id}/priority`, {
    method: 'PATCH',
    body: JSON.stringify({ priority }),
  });
}

export function assignSupportTicket(id: string, adminId: string | null) {
  return request<AdminSupportTicket>(`${API_BASE}/admin/support/tickets/${id}/assign`, {
    method: 'PATCH',
    body: JSON.stringify({ adminId }),
  });
}

export function deleteSupportTicket(id: string) {
  return request<void>(`${API_BASE}/admin/support/tickets/${id}`, { method: 'DELETE' });
}

// ── FAQ admin CMS ───────────────────────────────────────────────────────────

export type AdminFaqCategory =
  | 'GETTING_STARTED'
  | 'TEMPLATES'
  | 'INVITATIONS'
  | 'RSVP'
  | 'BILLING'
  | 'ACCOUNT'
  | 'TROUBLESHOOTING'
  | 'OTHER';

export interface AdminFaqArticle {
  id: string;
  title: string;
  slug: string;
  category: AdminFaqCategory;
  content: string;
  excerpt: string | null;
  tags: string[];
  isPublished: boolean;
  sortOrder: number;
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;
  publishedAt: string | null;
  author: { id: string; email: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListFaqParams {
  page?: number;
  limit?: number;
  category?: AdminFaqCategory;
  search?: string;
}

function faqQs(params: ListFaqParams = {}) {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.category) qs.set('category', params.category);
  if (params.search) qs.set('search', params.search);
  return qs.toString();
}

export function listFaqArticles(params: ListFaqParams = {}) {
  return request<Paginated<AdminFaqArticle>>(
    `${API_BASE}/admin/support/faqs?${faqQs(params)}`,
  );
}

export function getFaqArticle(id: string) {
  return request<AdminFaqArticle>(`${API_BASE}/admin/support/faqs/${id}`);
}

export interface CreateFaqArticlePayload {
  title: string;
  slug: string;
  category: AdminFaqCategory;
  content: string;
  excerpt?: string;
  tags?: string[];
  isPublished?: boolean;
  sortOrder?: number;
}
export type UpdateFaqArticlePayload = Partial<CreateFaqArticlePayload>;

export function createFaqArticle(payload: CreateFaqArticlePayload) {
  return request<AdminFaqArticle>(`${API_BASE}/admin/support/faqs`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export function updateFaqArticle(id: string, payload: UpdateFaqArticlePayload) {
  return request<AdminFaqArticle>(`${API_BASE}/admin/support/faqs/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
}

export function deleteFaqArticle(id: string) {
  return request<void>(`${API_BASE}/admin/support/faqs/${id}`, { method: 'DELETE' });
}
